import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { Notice, Timeline, type TimelineItem } from "../../../../design/components";
import { ApiError } from "../../../../api/client";
import type { ConnectionMethod, ProbeResult } from "../../../../api/types";
import { AgentTokensPanel } from "../../../companies/tabs/AgentTokensPanel";
import { PushPanel } from "../../../companies/tabs/PushPanel";
import { discoverEveryFamily, runProbe, useConnection } from "../onboardApi";
import styles from "../onboard.module.css";

const NOTHING_YET = "Nothing tested yet. Press Test connection.";

const STATE_BY_STATUS = { ok: "ok", failed: "fail", skipped: "warn" } as const;

function toItems(result: ProbeResult): TimelineItem[] {
  return result.steps.map((step) => ({
    title: step.title,
    at: `${step.ms} ms`,
    state: STATE_BY_STATUS[step.status] ?? "warn",
    detail: step.note,
  }));
}

export function StepTest({
  entityId,
  method,
  families,
  onContinue,
  onBack,
}: {
  entityId: string;
  method: ConnectionMethod;
  families: readonly string[];
  onContinue(): void;
  onBack(): void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // A push company is waiting for its ERP: the numbers refresh until the first push arrives.
  const connection = useConnection(entityId, method === "push" ? 5000 : undefined);
  const [result, setResult] = useState<ProbeResult | null>(null);
  const [discovered, setDiscovered] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  async function test(): Promise<void> {
    setRunning(true);
    setError("");
    setDiscovered(null);
    try {
      const outcome = await runProbe(entityId);
      setResult(outcome);
      // The probe is what makes this step complete, so the card the wizard reads is stale now.
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "connection"] });
      // Only Method 1 has fields to read; the other two probes are stubbed until their own stories.
      if (outcome.ok && method === "direct_api") {
        const perFamily = await discoverEveryFamily(entityId, families);
        setDiscovered(perFamily.reduce((total, one) => total + one.items.length, 0));
      }
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : t("onboard.test.runError", "The test could not be run."));
    } finally {
      setRunning(false);
    }
  }

  const failed = result?.steps.find((step) => step.status === "failed");
  const skipped = result?.steps.filter((step) => step.status === "skipped") ?? [];
  // Method 1 is only done once its fields are in hand; a failed discovery must not pass.
  const ready = Boolean(result?.ok) && !failed && (method !== "direct_api" || discovered !== null);

  return (
    <section className={styles.panel}>
      <h2>{t("onboard.test.title", "Test")}</h2>
      <p className={styles.caption}>
        {t("onboard.test.caption", "The hub runs the connection end to end and shows every step it took.")}
      </p>

      {method === "push" ? (
        <>
          <AgentTokensPanel entityId={entityId} silenceThresholdMinutes={15} canManage kind="push" />
          {connection.data?.push ? (
            <PushPanel push={connection.data.push} lastContactAt={connection.data.lastContactAt} />
          ) : null}
        </>
      ) : null}

      <button type="button" className="btn btn-primary" disabled={running} onClick={() => void test()}>
        {t("onboard.test.testConnection", "Test connection")}
      </button>

      <div className={styles.probe} role="status" aria-label={t("onboard.test.stepsLabel", "Connection test steps")}>
        {result ? <Timeline items={toItems(result)} /> : <p className={styles.empty}>{t("onboard.test.nothingYet", NOTHING_YET)}</p>}
        {discovered !== null ? (
          <p className={styles.discovered}>{t("onboard.test.fieldsDiscovered", "{{count}} fields discovered", { count: discovered })}</p>
        ) : null}
      </div>

      {error ? <Notice kind="fail">{error}</Notice> : null}
      {failed ? <Notice kind="fail">{failed.title} — {failed.note}</Notice> : null}
      {!failed && skipped.length > 0 ? (
        <Notice kind="warn">
          {t("onboard.test.notChecked", "Not checked: {{steps}}. Onboarding can continue.", {
            steps: skipped.map((step) => step.title).join(", "),
          })}
        </Notice>
      ) : null}

      <div className={styles.foot}>
        <button type="button" className="btn btn-primary" disabled={!ready} onClick={onContinue}>
          {t("onboard.test.continue", "Continue")}
        </button>
        {failed ? (
          <button type="button" className="btn" onClick={onBack}>
            {t("onboard.test.backToConnection", "Back to connection details")}
          </button>
        ) : null}
      </div>
    </section>
  );
}
